import { Stack, Box, Typography, Divider } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { orderBy } from "lodash";
import { grey } from "@mui/material/colors";

import IssueDetail from "./IssueDetail";
import MonitorHeader from "./MonitorHeader";
import { APIMonitoringEnrollment } from "../../../types/APIMonitoringEnrollment";
import { APIAnomalyStatus } from "../../../types/APIAnomaly";

export interface Props {
  monitor: APIMonitoringEnrollment;
  focusedNotificationId?: UUID | null | undefined;
}

export default function MonitorTimeline({
  monitor,
  focusedNotificationId,
}: Props) {
  // block lazy loading while we're scrolling, so we don't accidentally trigger a bunch of queries
  // while we're scrolling past
  const [suspendIssueLoading, setSuspendIssueLoading] = useState(
    focusedNotificationId != null
  );
  useEffect(() => {
    if (!focusedNotificationId) return;

    const el = document.getElementById(`issue_${focusedNotificationId}`);
    if (!el) return;

    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setSuspendIssueLoading(false);
          intersectionObserver.disconnect();
          return;
        }
      }
    });

    intersectionObserver.observe(el);

    el.scrollIntoView({
      behavior: "instant" as ScrollBehavior,
      block: "nearest",
    });
  }, [focusedNotificationId]);

  const openAnomalies = orderBy(
    monitor.anomalies.filter((a) => a.status === APIAnomalyStatus.OPEN),
    "created_at",
    "desc"
  );
  const resolvedAnomalies = orderBy(
    monitor.anomalies.filter((a) => a.status === APIAnomalyStatus.RESOLVED),
    "created_at",
    "desc"
  );

  return (
    <Box pb={4}>
      <MonitorHeader enrollment={monitor} />
      <Divider />
      <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, ml: 4 }}>
        Open Alerts
      </Typography>
      {openAnomalies.length === 0 ? (
        <Typography variant="h6" sx={{ pl: 7, color: grey[800] }}>
          None
        </Typography>
      ) : (
        openAnomalies.map((anomaly) => (
          <Fragment key={anomaly.id}>
            <Stack
              alignItems="center"
              sx={
                anomaly.id === focusedNotificationId
                  ? {
                      borderRadius: "10px",
                      border: "1px solid #ccc",
                      background: "rgba(30, 30, 30, 0.1)",
                    }
                  : {}
              }
              pt={1}
            >
              <IssueDetail
                id={`issue_${anomaly.id}`}
                issue={anomaly}
                focused={anomaly.id === focusedNotificationId}
                suspendLoading={
                  anomaly.id === focusedNotificationId
                    ? false
                    : suspendIssueLoading
                }
              />
            </Stack>
          </Fragment>
        ))
      )}
      <Divider sx={{ mt: "1em" }} />
      <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, ml: 4 }}>
        Resolved Alerts
      </Typography>
      {resolvedAnomalies.length === 0 ? (
        <Typography variant="h6" sx={{ pl: 7, color: grey[800] }}>
          None
        </Typography>
      ) : (
        resolvedAnomalies.map((anomaly) => (
          <Fragment key={anomaly.id}>
            <Stack
              alignItems="center"
              sx={
                anomaly.id === focusedNotificationId
                  ? {
                      borderRadius: "10px",
                      border: "1px solid #ccc",
                      background: "rgba(30, 30, 30, 0.1)",
                    }
                  : {}
              }
              pt={1}
            >
              <IssueDetail
                id={`issue_${anomaly.id}`}
                issue={anomaly}
                focused={anomaly.id === focusedNotificationId}
                suspendLoading={
                  anomaly.id === focusedNotificationId
                    ? false
                    : suspendIssueLoading
                }
              />
            </Stack>
          </Fragment>
        ))
      )}
    </Box>
  );
}
